"use client";

/**
 * CRM Settings Tab for Voice Agents
 *
 * Uses the CRM Abstraction Layer:
 * - Supports multiple CRM providers (Kommo, HubSpot, etc.)
 * - Uses crm_bindings for per-agent configuration
 * - Provider-agnostic UI that adapts to provider capabilities
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Link2,
  AlertCircle,
  Unplug,
  CheckCircle2,
  Loader2,
  Plus,
  XCircle,
} from "lucide-react";
import { useCrmBinding, type CrmStage } from "@/hooks/integrations/use-crm-binding";
import { useCrmIntegrations } from "@/hooks/integrations/use-crm-integrations";
import type { CrmProviderType } from "@/lib/crm/types";
import { AddCrmIntegrationDialog, CrmProviderLogo } from "@/components/crm";
import {
  IntegrationRequiredPlaceholder,
  IntegrationSectionLoadingPlaceholder,
} from "@/components/agents/shared/integration-required-placeholder";
import { SettingsTabLoader } from "./settings-tab-loader";
import { cn } from "@/lib/utils";
import { usePermissions } from "@/hooks/use-permissions";
import { CRM_PERMISSIONS } from "@/lib/auth/permissions.constants";

interface CrmSettingsProps {
  agentId: string;  // UUID
  compact?: boolean;
}

export function CrmSettings({ agentId, compact = false }: CrmSettingsProps) {
  const {
    binding,
    integrations,
    pipelines,
    selectedIntegration,
    isLoading,
    isLoadingPipelines,
    pipelinesError,
    isSaving,
    error,
    loadBinding,
    saveBinding,
    updateBindingConfig,
    getStagesForPipeline,
    // Tenant default info
    hasTenantDefault,
    tenantDefaultIntegration,
  } = useCrmBinding({
    scopeType: "voice_agent",
    scopeId: agentId,
    autoLoad: true,
  });

  // CRM integrations hook for adding new integrations and getting provider info
  const {
    providers,
    isConnecting,
    connectIntegration,
    prepareOAuthInstall,
    refreshProviders,
    getProvider,
    error: crmError,
    clearError: clearCrmError,
  } = useCrmIntegrations({ autoLoad: false });

  const { can } = usePermissions();

  // Local state
  const [pipelineStages, setPipelineStages] = useState<CrmStage[]>([]);
  const [showAddCrmDialog, setShowAddCrmDialog] = useState(false);

  // On initial load, auto-disable CRM if config is incomplete (no pipeline or stage)
  const hasNormalized = useRef(false);
  const wasLoading = useRef(false);
  useEffect(() => {
    if (isLoading) {
      wasLoading.current = true;
      return;
    }
    // Only fire after first load completes (loading → done transition)
    if (wasLoading.current && !hasNormalized.current) {
      hasNormalized.current = true;
      if (binding?.config?.enabled && (!binding.config.pipeline_id || !binding.config.voice?.stage_id)) {
        updateBindingConfig({ enabled: false });
      }
    }
  }, [isLoading, binding, updateBindingConfig]);

  // Load providers on mount (needed for Add CRM dialog and terminology)
  useEffect(() => {
    refreshProviders();
  }, [refreshProviders]);

  // Get terminology for the selected integration (or default)
  const terminology = selectedIntegration
    ? getProvider(selectedIntegration.provider)?.terminology
    : null;

  // Default terminology for CRM-neutral context
  const defaultTerminology = {
    opportunity: "opportunity",
    opportunityPlural: "opportunities",
    pipeline: "pipeline",
    stage: "stage",
  };

  // Use provider-specific terminology if available, otherwise default
  const t = terminology || defaultTerminology;

  // Helper to capitalize first letter
  const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

  // Handler for connecting new CRM
  const handleConnectCrm = useCallback(
    async (
      provider: CrmProviderType,
      name: string,
      credentials: Record<string, unknown>,
      metadata?: Record<string, unknown>
    ) => {
      const result = await connectIntegration(provider, name, credentials, metadata);
      if (result.success) {
        // Reload binding to refresh integrations list (via include_integrations=true)
        await loadBinding();
      }
      return result;
    },
    [connectIntegration, loadBinding]
  );

  // Get provider info for an integration
  const getProviderInfo = useCallback(
    (providerId: string) => {
      return providers.find((p) => p.id === providerId);
    },
    [providers]
  );

  // Load stages when pipeline changes
  useEffect(() => {
    if (binding?.config?.pipeline_id) {
      const stages = getStagesForPipeline(binding.config.pipeline_id);
      setPipelineStages(stages);
    } else {
      setPipelineStages([]);
    }
  }, [binding?.config?.pipeline_id, pipelines, getStagesForPipeline]);

  // Handle integration change
  const handleIntegrationChange = async (integrationId: string) => {
    if (integrationId === "none") {
      // Disable CRM for this agent (override tenant default)
      // Mark as explicitly_disabled to distinguish from "in setup" state
      if (binding) {
        // Update existing binding to disable
        await updateBindingConfig({ enabled: false, explicitly_disabled: true });
      } else if (integrations.length > 0) {
        // No binding exists - create one with enabled=false to override tenant default
        // Use first available integration as placeholder (won't be used since disabled)
        await saveBinding(integrations[0].id, {
          enabled: false,
          explicitly_disabled: true,
        });
      }
      return;
    }

    // Create/update binding with new integration
    // Clear explicitly_disabled since user is selecting an integration
    await saveBinding(integrationId, {
      enabled: false, // Disabled until pipeline/stage is selected
      explicitly_disabled: false,
      pipeline_id: undefined,
      voice: {
        stage_id: undefined,
      },
    });
  };

  // Handle pipeline change
  const handlePipelineChange = async (pipelineId: string) => {
    await updateBindingConfig({
      pipeline_id: pipelineId,
      voice: {
        ...binding?.config?.voice,
        stage_id: undefined,
      },
    });
  };

  // Handle stage change
  const handleStageChange = async (stageId: string) => {
    await updateBindingConfig({
      voice: {
        ...binding?.config?.voice,
        stage_id: stageId,
      },
    });
  };

  // Handle enable toggle
  const handleEnableToggle = async (enabled: boolean) => {
    if (enabled && !binding && integrations.length > 0) {
      // Create binding with first available integration when enabling
      await saveBinding(integrations[0].id, {
        enabled: true,
        explicitly_disabled: false,
        pipeline_id: undefined,
        voice: {
          stage_id: undefined,
        },
      });
    } else if (enabled && binding) {
      // Enable existing binding
      await updateBindingConfig({ enabled: true, explicitly_disabled: false });
    } else {
      // Disable
      await updateBindingConfig({ enabled: false });
    }
  };

  const getProviderDisplayName = (provider: string) => {
    const names: Record<string, string> = {
      kommo: "Kommo",
      hubspot: "HubSpot",
      salesforce: "Salesforce",
      pipedrive: "Pipedrive",
    };
    return names[provider] || provider;
  };

  const bindingConfig = binding?.config || {};
  const voiceConfig = bindingConfig.voice || {};
  const crmEnabled = bindingConfig.enabled ?? false;
  const hasStageSelected = !!voiceConfig.stage_id;
  const canEnableCrm = hasStageSelected;
  // Check if user explicitly disabled CRM by selecting "None"
  const isExplicitlyDisabled = !!bindingConfig.explicitly_disabled;
  // For Select value: show "none" if no binding OR explicitly disabled
  const selectValue = (!binding || isExplicitlyDisabled) ? "none" : binding?.integrationId;

  // Filter active stages
  const activeStages = pipelineStages.filter((s) => !s.isArchived);

  if (!can(CRM_PERMISSIONS.VIEW)) {
    return (
      <Card className={compact ? "min-h-80" : undefined}>
        <CardHeader>
          <CardTitle>Access Denied</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertDescription>
              You don&apos;t have permission to access CRM settings. Contact your administrator.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // Show loader until config is loaded
  if (isLoading) {
    if (compact) {
      return (
        <IntegrationSectionLoadingPlaceholder
          icon={Link2}
          title="CRMs"
          description="Connect a CRM and choose how this agent uses it."
        />
      );
    }
    return <SettingsTabLoader message="Loading CRM settings..." />;
  }

  if (integrations.length === 0) {
    return (
      <>
        <IntegrationRequiredPlaceholder
          icon={Link2}
          showIllustration={false}
          sectionIcon={Link2}
          sectionTitle="CRMs"
          sectionDescription="Connect a CRM and choose how this agent uses it."
          iconTestId="crm-integration-required-icon"
          title="Connect a CRM First"
          description="No CRM is connected for this workspace. Connect one before choosing where this agent creates customer records and call notes."
          supportingText="Your CRM connection is managed here and can then be selected for this agent."
          action={(
            <Button type="button" onClick={() => setShowAddCrmDialog(true)}>
              <Plus className="mr-2 h-4 w-4" />
              <span>Connect CRM</span>
            </Button>
          )}
        />
        <AddCrmIntegrationDialog
          open={showAddCrmDialog}
          onOpenChange={setShowAddCrmDialog}
          providers={providers}
          onConnect={handleConnectCrm}
          onPrepareOAuth={prepareOAuthInstall}
          isConnecting={isConnecting}
          error={crmError}
          onClearError={clearCrmError}
        />
      </>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* CRM Connection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5 shrink-0" />
            <span>CRM Integration</span>
          </CardTitle>
          <CardDescription>
            Connect this agent to your CRM to create opportunities and add call notes.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-4 gap-y-2 rounded-lg border bg-background p-4">
            <Label htmlFor={`agent-crm-enabled-${agentId}`} className="text-base font-medium [overflow-wrap:anywhere]">
              Enable CRM for this agent
            </Label>
            <Switch
              id={`agent-crm-enabled-${agentId}`}
              aria-describedby={`agent-crm-enabled-description-${agentId}`}
              checked={crmEnabled}
              onCheckedChange={handleEnableToggle}
              disabled={isSaving}
              className="shrink-0"
            />
            <p id={`agent-crm-enabled-description-${agentId}`} className="col-span-2 text-sm text-muted-foreground [overflow-wrap:anywhere]">
              <span>Allow this agent to create</span> {t.opportunityPlural} <span>and add call notes in your CRM.
              Choose a CRM connection,</span> {t.pipeline}<span>, and</span> {t.stage} <span>below to finish setup.</span>
            </p>
          </div>
          <div className="space-y-6" hidden={!crmEnabled}>
          <div className="space-y-4">
              {/* CRM Connection Selection */}
              <div className="space-y-2">
                <Label>CRM Connection</Label>
                <Select
                  value={selectValue}
                  onValueChange={handleIntegrationChange}
                  disabled={isSaving}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select CRM account..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      <div className="flex items-center gap-2">
                        <Unplug className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-500">None / Disabled</span>
                      </div>
                    </SelectItem>
                    {integrations.map((integration) => {
                      const providerInfo = getProviderInfo(integration.provider);
                      return (
                        <SelectItem key={integration.id} value={integration.id}>
                          <div className="flex items-center gap-2">
                            {providerInfo?.logoUrl ? (
                              <CrmProviderLogo
                                provider={integration.provider}
                                logoUrl={providerInfo.logoUrl}
                                size={16}
                              />
                            ) : (
                              <div
                                className={cn(
                                  "w-4 h-4 rounded-full",
                                  integration.isActive ? "bg-green-500" : "bg-gray-300"
                                )}
                              />
                            )}
                            <span>
                              {integration.name}{" "}
                              <span className="text-gray-400">
                                ({getProviderDisplayName(integration.provider)})
                              </span>
                            </span>
                            {!integration.isActive && (
                              <Badge variant="secondary" className="text-xs ml-1">
                                Disabled
                              </Badge>
                            )}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Select "None" to disable CRM for this agent (overrides tenant default).
                </p>
              </div>

              {/* Add another CRM button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAddCrmDialog(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                <span>Or add a different CRM</span>
              </Button>

              {/* Connection Status - only show when CRM is enabled */}
              {crmEnabled && selectedIntegration && !pipelinesError && (
                <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-800">
                    Connected to {selectedIntegration.name} ({getProviderDisplayName(selectedIntegration.provider)})
                  </span>
                </div>
              )}

              {pipelinesError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <XCircle className="w-4 h-4 text-red-600" />
                  <span className="text-sm text-red-800">{pipelinesError}</span>
                </div>
              )}

              {/* Tenant Default Inheritance Notice - show when no agent binding but tenant default exists */}
              {!binding && hasTenantDefault && tenantDefaultIntegration && (
                <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-blue-600" />
                  <span className="text-sm text-blue-800">
                    Using tenant default: {tenantDefaultIntegration.name} ({getProviderDisplayName(tenantDefaultIntegration.provider)}).
                    Select a specific integration or "None" to override.
                  </span>
                </div>
              )}
          </div>

          {crmEnabled && (
            <div className="space-y-6 border-t pt-6">
              <div>
                <h3 className="font-semibold">{capitalize(t.opportunity)} <span>Settings</span></h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  <span>Configure where new</span> {t.opportunityPlural} <span>are created for calls.</span>
                </p>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>{capitalize(t.pipeline)}</Label>
                  {isLoadingPipelines ? (
                    <div className="flex items-center gap-2 p-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm text-gray-500">Loading {t.pipeline}s...</span>
                    </div>
                  ) : (
                    <Select
                      value={bindingConfig.pipeline_id || ""}
                      onValueChange={handlePipelineChange}
                      disabled={isSaving}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={`Select ${t.pipeline}...`} />
                      </SelectTrigger>
                      <SelectContent>
                        {pipelines.map((pipeline) => (
                          <SelectItem key={pipeline.id} value={pipeline.id}>
                            {pipeline.name} <span>{pipeline.isDefault && "(Default)"}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>{capitalize(t.stage)}</Label>
                  <Select
                    value={voiceConfig.stage_id || ""}
                    onValueChange={handleStageChange}
                    disabled={!bindingConfig.pipeline_id || isSaving}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={`Select ${t.stage}...`} />
                    </SelectTrigger>
                    <SelectContent>
                      {activeStages.map((stage) => (
                        <SelectItem key={stage.id} value={stage.id}>
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: stage.color || "#gray" }}
                            />
                            <span>{stage.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {!canEnableCrm && (
                <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-yellow-600" />
                  <span className="text-sm text-yellow-800">
                    Select {t.pipeline} and {t.stage} to enable CRM integration for calls.
                  </span>
                </div>
              )}
            </div>
          )}
          </div>
        </CardContent>
      </Card>

      {/* Add CRM Integration Dialog */}
      <AddCrmIntegrationDialog
        open={showAddCrmDialog}
        onOpenChange={setShowAddCrmDialog}
        providers={providers}
        onConnect={handleConnectCrm}
        onPrepareOAuth={prepareOAuthInstall}
        isConnecting={isConnecting}
        error={crmError}
        onClearError={clearCrmError}
      />
    </div>
  );
}
